import { useEffect, useRef, useState, ClipboardEvent as ReactClipboardEvent } from 'react'

import { Button, Divider, Flex, HStack, PinInput, PinInputField, Text } from '@chakra-ui/react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'

import { useInitResetPasswordMutation, useValidateResetPasswordMutation } from '@core/app/api/authApi'
import { email, nextForgotStep, prevForgotStep, setCode } from '@core/app/slices/auth'
import { AppButton } from '@core/components/AppButton'
import { useAppTheme } from '@core/utils/contexts/ThemeContext/ThemeContext'
import { useAppDispatch, useAppSelector } from '@core/utils/hooks/hook'

const resetTime = 300

export const EmailValidation = () => {
    const dispatch = useAppDispatch()
    const emailValue = useAppSelector(email)
    const [validate] = useValidateResetPasswordMutation()
    const [initResetPassword] = useInitResetPasswordMutation()
    const [count, setCount] = useState<number>(resetTime)
    const [err, setErr] = useState<string>('')
    const [pinValue, setPinValue] = useState<string>('')
    const { executeRecaptcha } = useGoogleReCaptcha()

    const lastPinInput = useRef<HTMLInputElement>(null)

    const { t } = useTranslation()

    const getEmailVerificationHandler = async () => {
        if (!executeRecaptcha) {
            console.error('ReCAPTCHA has not loaded yet')
            return
        }

        const token = await executeRecaptcha('api_reset_password_init')

        if (token) {
            try {
                await initResetPassword({
                    data: {
                        email: emailValue,
                        'g-recaptcha-response': token,
                    },
                }).unwrap()
                setCount(resetTime)
            } catch (error: any) {
                if (error?.status === 422) {
                    const apiMessage =
                        error?.data?.message || 'Invalid Recaptcha. Please, reload the page and try again.'
                    setErr(apiMessage)
                } else {
                    setErr(error.data?.errors?.code?.[0] || 'unknown error')
                }
            }
        }
    }

    const sendEmailVerificationCodeHandler = async (code: string) => {
        try {
            await validate({
                data: {
                    email: emailValue,
                    code: code.toUpperCase(),
                },
            }).unwrap()

            dispatch(setCode(code))
            dispatch(nextForgotStep('newPassword'))
        } catch (error: any) {
            console.log(error)
            setErr(error.data?.errors?.code?.[0] || t('5858', 'Something went wrong'))

            setTimeout(() => {
                dispatch(prevForgotStep('forgotPassword'))
            }, 1500)
        }
    }
    const handlePaste = (e: ReactClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        try {
            const pastedText = e.clipboardData.getData('text')
            const cleanedText = pastedText.replace(/-/g, '').toUpperCase()

            const truncatedText = cleanedText.slice(0, 4)

            setPinValue(truncatedText)
            setErr('')

            if (truncatedText.length === 4) {
                sendEmailVerificationCodeHandler(truncatedText)
            }
        } catch (error: any) {
            setErr(error.data?.errors?.code?.[0] || error.message || t('5858', 'Something went wrong'))
        }
    }

    useEffect(() => {
        if (count === 0) return

        const interval = setInterval(() => {
            setCount((prev) => prev - 1)
        }, 1000)
        return () => clearInterval(interval)
    }, [count])

    return (
        <Flex flexDirection={'column'}>
            <Text fontSize={'xs'} color={'text.thirdly'} mb={1}>
                {t('6593')} 2 {t('1165')} 4
            </Text>
            <Text fontSize={'2xl'} color={'text.main'} fontWeight={'semibold'} mb={4}>
                {t('6627')}
            </Text>
            <Text color={'text.secondary'} fontWeight={'medium'}>
                {t('6588')}
            </Text>
            <Text variant={'noMargins'} color={'text.link'} fontWeight={'bold'}>
                {emailValue}
            </Text>
            <HStack mt={10}>
                <PinInput
                    otp
                    placeholder={''}
                    size={'lg'}
                    value={pinValue}
                    autoFocus={true}
                    type={'alphanumeric'}
                    isInvalid={err.length > 0}
                    focusBorderColor={'state.primary'}
                    errorBorderColor={'formElements.borderError'}
                    onComplete={(code) => {
                        lastPinInput.current?.blur()
                        sendEmailVerificationCodeHandler(code)
                    }}
                    onChange={(e) => {
                        setPinValue(e.toUpperCase())
                        setErr('')
                    }}
                >
                    <PinInputField w={'51px'} h={'60px'} onPaste={handlePaste} />
                    <PinInputField w={'51px'} h={'60px'} onPaste={handlePaste} />
                    <Text> – </Text>
                    <PinInputField w={'51px'} h={'60px'} onPaste={handlePaste} />
                    <PinInputField ref={lastPinInput} w={'51px'} h={'60px'} onPaste={handlePaste} />
                </PinInput>
            </HStack>
            {err && (
                <Text fontSize={'xs'} color={'text.error'} mt={1}>
                    {err}
                </Text>
            )}
            <Text fontSize={'sm'} color={'text.thirdly'} mt={2}>
                {t('6589')}{' '}
                <Text as="b" color={'text.secondary'}>
                    {count}
                </Text>{' '}
                {t('6590')}
            </Text>
            <Divider mt={'112px'} />
            <Text fontSize={'sm'} fontWeight={'medium'} color={'text.thirdly'} mt={6}>
                {t('6591')}.
            </Text>
            <Flex gap={'8px'} alignItems={'center'} justifyContent={'space-between'} mt={3}>
                <Button
                    variant={'outline'}
                    minHeight={'48px'}
                    minWidth={'120px'}
                    _hover={{ bg: '#2F80ED', color: 'text.white' }}
                    onClick={() => dispatch(prevForgotStep('forgotPassword'))}
                >
                    {t('8782')}
                </Button>
                {/*<Button*/}
                {/*    type={'submit'}*/}
                {/*    variant={'primary'}*/}
                {/*    minHeight={'48px'}*/}
                {/*    minWidth={'120px'}*/}
                {/*    alignSelf={'start'}*/}
                {/*    onClick={() => getEmailVerificationHandler()}*/}
                {/*    isDisabled={count > 0}*/}
                {/*>*/}
                {/*    {t('8783')}*/}
                {/*</Button>*/}
                <AppButton
                    type={'submit'}
                    variant={'primary'}
                    minHeight={'48px'}
                    minWidth={'120px'}
                    alignSelf={'start'}
                    onClick={() => getEmailVerificationHandler()}
                    isDisabled={count > 0}
                    title={t('8783')}
                ></AppButton>
            </Flex>
        </Flex>
    )
}
